import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { startCheckout } from "@/utils/checkout";
import { API } from "@/api";
import { Sparkles, KeyRound, CheckCircle2, AlertCircle } from "lucide-react";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [licenseCode, setLicenseCode] = useState("");
  const [licenseStatus, setLicenseStatus] = useState(null); // {valid, tier_name} | {error}
  const [licenseChecking, setLicenseChecking] = useState(false);
  const [busy, setBusy] = useState(false);

  const checkLicense = async () => {
    const code = licenseCode.trim().toUpperCase();
    if (!code) return setLicenseStatus(null);
    setLicenseChecking(true);
    try {
      const { data } = await axios.post(`${API}/auth/validate-license`, { code });
      setLicenseStatus({ ok: true, tier_name: data.tier_name, entitlements: data.entitlements });
    } catch (e) {
      const msg = e.response?.data?.detail || "Could not validate code";
      setLicenseStatus({ ok: false, error: typeof msg === "string" ? msg : "Invalid license code" });
    } finally {
      setLicenseChecking(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (!licenseCode.trim()) return toast.error("A license code is required to create an account");
    setBusy(true);
    const res = await signup(email, password, fullName, licenseCode.trim().toUpperCase());
    if (!res.ok) {
      setBusy(false);
      return toast.error(res.error);
    }
    toast.success("Account created — your plan is now active");
    const pending = sessionStorage.getItem("vbs_pending_tier");
    if (pending && searchParams.get("next") === "checkout") {
      sessionStorage.removeItem("vbs_pending_tier");
      try {
        await startCheckout(pending);
        return;
      } catch (err) {
        toast.error("Could not start checkout");
      }
    }
    setBusy(false);
    nav("/dashboard");
  };

  return (
    <div className="vbs-auth-bg" style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
      <div className="vbs-card" style={{ width: "100%", maxWidth: 460, padding: 36 }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <span className="vbs-logo-dot" />
            <span style={{ fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 700, color: "var(--white)" }}>
              Viral Book Suite
            </span>
          </div>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: 2, color: "var(--gold-dim)", textTransform: "uppercase" }}>
            Create your account
          </div>
        </div>

        <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <label>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--gold-dim)", letterSpacing: 1.5, marginBottom: 6, textTransform: "uppercase" }}>Name</div>
            <input data-testid="signup-name" className="vbs-input" type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} required />
          </label>
          <label>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--gold-dim)", letterSpacing: 1.5, marginBottom: 6, textTransform: "uppercase" }}>Email</div>
            <input data-testid="signup-email" className="vbs-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </label>
          <label>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--gold-dim)", letterSpacing: 1.5, marginBottom: 6, textTransform: "uppercase" }}>Password</div>
            <input data-testid="signup-password" className="vbs-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />
          </label>
          <label>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
              <span style={{ fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--gold)", letterSpacing: 1.5, textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 5 }}>
                <KeyRound size={11} /> License Code
              </span>
              {licenseChecking ? (
                <span style={{ fontSize: 10, color: "var(--muted)" }}>checking…</span>
              ) : licenseCode.trim() ? (
                <button
                  type="button"
                  onClick={checkLicense}
                  style={{ fontSize: 10, color: "var(--gold)", background: "none", border: "none", cursor: "pointer", textTransform: "uppercase", letterSpacing: 1.5 }}
                  data-testid="signup-license-check"
                >
                  Verify
                </button>
              ) : null}
            </div>
            <input
              data-testid="signup-license-code"
              className="vbs-input"
              type="text"
              value={licenseCode}
              onChange={(e) => { setLicenseCode(e.target.value.toUpperCase()); setLicenseStatus(null); }}
              onBlur={checkLicense}
              required
              placeholder="VBS-XXXX-XXXX-XXXX"
              style={{ fontFamily: "var(--font-mono)", letterSpacing: 1 }}
            />
            {licenseStatus?.ok && (
              <div data-testid="license-ok" style={{ marginTop: 8, padding: "8px 10px", borderRadius: 8, background: "rgba(109,212,160,0.08)", border: "1px solid rgba(109,212,160,0.35)", color: "#6dd4a0", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }}>
                <CheckCircle2 size={13} /> Unlocks <strong style={{ marginLeft: 4 }}>{licenseStatus.tier_name}</strong>
              </div>
            )}
            {licenseStatus?.ok === false && (
              <div data-testid="license-err" style={{ marginTop: 8, padding: "8px 10px", borderRadius: 8, background: "rgba(224,80,64,0.08)", border: "1px solid rgba(224,80,64,0.35)", color: "#ff8f80", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 }}>
                <AlertCircle size={13} /> {licenseStatus.error}
              </div>
            )}
          </label>
          <button data-testid="signup-submit" type="submit" disabled={busy || licenseStatus?.ok === false} className="vbs-btn vbs-btn-gold" style={{ padding: 14, marginTop: 6 }}>
            {busy ? "Creating…" : "Create account"}
            <Sparkles size={15} />
          </button>
          <div style={{ fontSize: 11, color: "var(--muted)", textAlign: "center", marginTop: 2 }}>
            New customers receive their license code by email after purchase. Contact <a href="mailto:hello@viralbooksuite.com" style={{ color: "var(--gold)" }}>hello@viralbooksuite.com</a> if you need help.
          </div>
        </form>

        <div style={{ marginTop: 22, textAlign: "center", fontSize: 13, color: "var(--muted)" }}>
          Already have an account? <Link to="/login" data-testid="login-link">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
